/** Свежесть витрины по источникам (WB: `mart.data_through` — на каждое юрлицо): всплывашка у
 *  строки «Витрина собрана … данные …». Дата источника null — «нет данных», а не пропуск; ключа
 *  источника в ответе нет вовсе (undefined) — жёлтым «нет в ответе витрины»: это расхождение с
 *  контрактом, а не отсутствие данных. */
import React from "react";

import type { DataThrough } from "@/api/ozonAnalytics";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtWhen } from "./format";

/** Источники по порядку: подпись → дата(ы) (ключ `data_through` и подпись перед датой). У
 *  реализации — две даты: недельные и ежедневные. */
const SOURCES: { label: string; parts: { key: keyof DataThrough; prefix?: string }[] }[] = [
  {
    label: "Реализация",
    parts: [
      { key: "realization_weekly_to", prefix: "недельные по " },
      { key: "realization_daily_to", prefix: "ежедневные по " },
    ],
  },
  { label: "Хранение", parts: [{ key: "storage_date" }] },
  { label: "Реклама", parts: [{ key: "adverts_date" }] },
  { label: "Карточки", parts: [{ key: "cards_snapshot_date" }] },
  { label: "Баланс", parts: [{ key: "balance_snapshot_date" }] },
  { label: "Лента заказов", parts: [{ key: "order_feed_loaded_at" }] },
];

/** Дата источника: null — «нет данных», ключа в ответе нет — жёлтая пометка. */
function SourceValue({
  d,
  part,
}: {
  d: DataThrough;
  part: { key: keyof DataThrough; prefix?: string };
}): React.ReactElement {
  const v = (d as Partial<DataThrough>)[part.key];
  return (
    <>
      {part.prefix}
      {v === undefined ? (
        <span
          className="text-amber-700 dark:text-amber-400"
          title={`ключа ${part.key} нет в data_through витрины`}
          data-testid="source-missing-key"
        >
          нет в ответе витрины
        </span>
      ) : (
        fmtWhen(v)
      )}
    </>
  );
}

export function SourcesFreshness({
  dataThrough,
}: {
  dataThrough: Record<string, DataThrough>;
}): React.ReactElement {
  const legals = Object.keys(dataThrough).sort((a, b) => a.localeCompare(b, "ru"));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded-full border px-2 py-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          data-testid="sources-freshness-toggle"
        >
          по источникам
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-[min(560px,90vw)] p-3 text-xs">
        <div className="space-y-3" data-testid="sources-freshness">
          {legals.length === 0 && (
            <div className="text-muted-foreground">Витрина не отдала свежесть источников.</div>
          )}
          {legals.map((l) => {
            const d = dataThrough[l]!;
            return (
              <section key={l} className="space-y-1" data-testid="sources-legal">
                <h4 className="font-medium">{l}</h4>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                  {SOURCES.map((s) => (
                    <React.Fragment key={s.label}>
                      <dt className="text-muted-foreground">{s.label}</dt>
                      <dd className="tabular-nums">
                        {s.parts.map((part, i) => (
                          <React.Fragment key={part.key}>
                            {i > 0 && " · "}
                            <SourceValue d={d} part={part} />
                          </React.Fragment>
                        ))}
                      </dd>
                    </React.Fragment>
                  ))}
                </dl>
              </section>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
